import { ApiResponse } from "../../../core/_models";
import { requestLaravel } from "../../../../../utils/api";

const PROJECT_FIELD_SECTION_GET = "/project-fields/section/get";
const PROJECT_FIELD_SECTION_SAVE = "/project-fields/section/save";
const sectionMetaKeys = new Set(["project_id", "formAction", "iscompleted"]);

export interface ProjectFieldDefinition {
    field_key: string;
    label: string;
    data_type: string;
    input_type: string;
    lookup_dev_name: string | null;
    sort_order: number;
    is_required_for_gate: boolean;
    validation_rules: string[];
    value: unknown;
    free_text?: Record<string, string>;
    options?: Array<{ data_key: string; display_name: string }>;
}

export interface ProjectFieldSectionNode {
    section_key: string;
    label: string;
    description: string | null;
    help_text: string | null;
    sort_order: number;
    fields: ProjectFieldDefinition[];
    sub_sections: ProjectFieldSectionNode[];
}

export interface ProjectFieldSection extends ProjectFieldSectionNode {
    project_id: number;
    stage_id: number;
}

function isRemarkMap(value: unknown): value is Record<string, string> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

function packSectionFields(payload: object): Record<string, unknown> {
    const remarks = new Map<string, Record<string, string>>();
    const fields: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(payload)) {
        if (sectionMetaKeys.has(key) || value === undefined) {
            continue;
        }

        if (key.endsWith("_remarks") && isRemarkMap(value)) {
            remarks.set(key.slice(0, -"_remarks".length), value);
            continue;
        }

        fields[key] = value;
    }

    remarks.forEach((remarkMap, fieldKey) => {
        const selected = fields[fieldKey];
        if (!Array.isArray(selected)) {
            return;
        }

        fields[fieldKey] = selected.map((item) => {
            if (typeof item !== "string") {
                return item;
            }

            const freeText = remarkMap[item];
            return freeText ? { key_value: item, free_text: freeText } : item;
        });
    });

    return fields;
}

export function flattenProjectFieldValues(section?: ProjectFieldSectionNode | null): Record<string, unknown> {
    const values: Record<string, unknown> = {};

    const walk = (node?: ProjectFieldSectionNode | null): void => {
        if (!node) {
            return;
        }

        for (const field of node.fields ?? []) {
            if (!field?.field_key) {
                continue;
            }

            values[field.field_key] = field.value ?? null;
            if (isRemarkMap(field.free_text)) {
                values[`${field.field_key}_remarks`] = field.free_text;
            }
        }

        for (const child of node.sub_sections ?? []) {
            walk(child);
        }
    };

    walk(section);
    return values;
}

export function withProjectFieldValues<T>(response: ApiResponse<ProjectFieldSection>): ApiResponse<T> {
    if (response.status !== "ok" || !response.data || !Array.isArray(response.data.fields)) {
        return response as ApiResponse<T>;
    }

    return {
        ...response,
        data: flattenProjectFieldValues(response.data) as T,
    };
}

export function getProjectFieldSection(
    projectId: string | number,
    stageId: number,
    sectionKey: string,
    signal?: AbortSignal,
): Promise<ApiResponse<ProjectFieldSection>> {
    return requestLaravel<ApiResponse<ProjectFieldSection>>(
        PROJECT_FIELD_SECTION_GET,
        {
            project_id: Number(projectId),
            stage_id: stageId,
            section_key: sectionKey,
        },
        { signal },
    );
}

export function saveProjectFieldSection(
    projectId: string | number,
    stageId: number,
    sectionKey: string,
    payload: object,
    signal?: AbortSignal,
): Promise<ApiResponse<ProjectFieldSection>> {
    return requestLaravel<ApiResponse<ProjectFieldSection>>(
        PROJECT_FIELD_SECTION_SAVE,
        {
            project_id: Number(projectId),
            stage_id: stageId,
            section_key: sectionKey,
            fields: packSectionFields(payload),
        },
        { signal },
    );
}
